import React from 'react';

// SHOP-171 fix: item.enddate comes from a MySQL DATE column (date-only, e.g.
// "2026-08-20"), unlike item.createddate which is a full DATETIME string.
// The JS Date constructor parses a date-only ISO string as UTC midnight, so
// in any timezone behind UTC `new Date(item.enddate)` silently rolls back to
// the previous calendar day once formatted in local time (e.g. "2026-08-20"
// rendered as "19 de agosto" in UTC-4). Appending a local midnight marker
// forces the same string to parse as local time instead, matching how a
// full datetime string already behaves.
const parseDateOnlyAsLocal = (dateStr) => (
    dateStr && !dateStr.includes('T') ? new Date(`${dateStr}T00:00:00`) : new Date(dateStr)
);

// SHOP-128: "on offer" (item.special) and "has a listed regular price to
// compare against" (item.regular_price) are independent facts about an item
// - special is the bundle mechanic text (e.g. "4x$3"), regular_price is the
// pre-discount reference price the backend now includes (DAO/DBDao.py
// get_cheapest_items_by_category). No minimum-savings threshold: any
// regular_price > price shows the badge, per the ticket's own decision.
// SHOP-172: never applies to an expired-with-regular_price item - there's no
// "discount" being offered there, the regular_price is just the current
// price now that the special has lapsed (see isExpiredRegularPrice below).
const getSavingsPercent = (item) => {
    if (item?.is_regular_price) return null;
    if (item?.regular_price == null || item.regular_price <= item.price) return null;
    return Math.round(((item.regular_price - item.price) / item.regular_price) * 100);
};

// SHOP-172: item.price is now always the raw, possibly-expired price (see
// DAO/DBDao.py get_cheapest_items_by_category's "original_price" column) -
// null/0 is the documented edge case for an expired item whose original
// price was never usable to begin with, per the ticket's own risk note.
const hasUsablePrice = (value) => value != null && value !== 0;

const savingsPillStyle = {
    display: 'inline-block',
    fontSize: '0.75em',
    fontWeight: 700,
    borderRadius: '999px',
    padding: '2px 8px',
    marginTop: '2px',
    background: '#dcfce7',
    color: '#166534',
};

const regularPriceStrikeStyle = {
    fontSize: '0.85em',
    color: '#6c757d',
    textDecoration: 'line-through',
    marginBottom: '2px',
};

// SHOP-135: extracted out of productscategories.js (its only call site
// before this ticket) so ShoppingListModal can reuse the same item-detail
// rendering for its own "view detail" overlay, instead of duplicating it.
const ProductDetails = ({ item, isMain = true, translations, language, isSelected, formatCurrency }) => {
    if (!item) return null;

    const hasSpecialOffer = item?.special && item.special !== 'No';
    const savingsPercent = getSavingsPercent(item);
    const locale = language === 'es' ? 'es-ES' : 'en-US';
    // SHOP-172: an expired item shown at regular_price (SHOP-147/SHOP-153)
    // now gets the reversed treatment - the original (stale) item.price
    // struck through, item.regular_price clean as the current price -
    // instead of the single-value + "Reg." tag treatment SHOP-153/154 used.
    //
    // SHOP-179 fix: also requires a usable item.regular_price. is_regular_price
    // is set true by two different producers: the catalog flow (DAO/DBDao.py
    // get_cheapest_items_by_category), which always pairs it with a real
    // regular_price, and productscategories.js's handleCopilotItemSelect
    // (SHOP-177), which maps DAO/CopilotDao.py's single-price buscar_items
    // shape (item_id/productid/precio/activo - no regular_price at all) and
    // sets is_regular_price purely to retrigger the SHOP-176 "price may be
    // stale" toast on add-to-cart - it never had a second price value to put
    // in regular_price. Without this guard, opening such an item's detail
    // view hit this branch with regular_price undefined and rendered "$NaN"
    // (live bug report, 2026-08-25). Falling back to the plain-price branch
    // below is correct here: getSavingsPercent already returns null whenever
    // is_regular_price is true (regardless of this guard), so the fallback
    // renders item.price cleanly with no strikethrough/badge - the "may be
    // stale" fact is still surfaced via the add-to-cart toast, just not
    // duplicated in this view when there's no second price to contrast it
    // with.
    const isExpiredRegularPrice = !!item.is_regular_price && hasUsablePrice(item.regular_price);
    const showOriginalPriceStrike = isExpiredRegularPrice && hasUsablePrice(item.price);

    // --- RENDERIZADO COMPACTO PARA ARTÍCULOS ALTERNATIVOS (MINIMALISTA) ---
    if (!isMain) {
     // SHOP-179: item.enddate is genuinely absent (not just unparsed) for a
     // Copilot chat item (productscategories.js's handleCopilotItemSelect
     // hardcodes enddate: null, since DAO/CopilotDao.py's buscar_items row
     // shape has no enddate field at all) - new Date(null)/new Date(undefined)
     // parse as the Unix epoch / Invalid Date rather than throwing, so without
     // this guard the date line silently rendered "31 de diciembre de 1969"
     // (live bug report, 2026-08-25) instead of just not rendering. Formatear
     // la fecha según el idioma actual, solo si hay una fecha real.
        const formattedDate = item.enddate
            ? parseDateOnlyAsLocal(item.enddate).toLocaleDateString(locale, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
            : null;
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '0px 0px', // Padding reducido
                // El fondo de oferta especial/seleccionado ahora lo pinta el wrapper
                // exterior (el "card" real, con el padding/borde visibles) - ver SHOP-13.
                backgroundColor: 'transparent',
                transition: 'background-color 0.2s'
            }}>
                {/* Nombre, Tienda, Unidad y Descripción */}
                <div style={{ flexGrow: 1, minWidth: '0' }}>
                     <h4 className="product-name" style={{
                       fontSize: '1.25em', fontWeight: 'bold'
                    }}>{item.brand?.trim() ? `${item.name} (${item.brand})` : item.name}</h4>

                    {/* Nueva línea para la descripción */}
                       <p style={{
                           fontSize: '1em',
                           marginTop:"0px"
                        }}>
                        {item.detail}
                    </p>
                    <span style={{ fontSize: '1em', color: '#6c757d', display: 'block' }}>
                        🛍️ {item.store} - 📦 {item.unit}
                        {/* Uso de la traducción */}
                        {hasSpecialOffer && <span style={{ marginLeft: '8px', color: '#ff5722' }}>✨ {translations.itemOffer} {item.special}</span>}
                    </span>
                    {/* Fecha */}
                    {formattedDate && (
                        <span className="product-date" style={{fontSize: '1em', padding: '0' ,display: 'flex'}}>
                            {/* Uso de la traducción */}
                            <span className="icon" >📅</span>{formattedDate}
                        </span>
                    )}

                </div>

                {/* Precio y estado de selección */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', marginLeft: '10px', flexShrink: 0 }}>
                    {isExpiredRegularPrice ? (
                        <>
                            {showOriginalPriceStrike && (
                                <span style={regularPriceStrikeStyle}>{formatCurrency(item.price)}</span>
                            )}
                            <span style={{
                                fontSize: '1em',
                                fontWeight: '700',
                                color: '#28a745', // Color verde para precio alternativo
                                marginBottom: '4px'
                            }}>
                                {formatCurrency(item.regular_price)}
                            </span>
                        </>
                    ) : (
                        <>
                            {savingsPercent != null && (
                                <span style={regularPriceStrikeStyle}>{formatCurrency(item.regular_price)}</span>
                            )}
                            <span style={{
                                fontSize: '1em',
                                fontWeight: '700',
                                color: '#28a745', // Color verde para precio alternativo
                                marginBottom: '4px'
                            }}>
                                {formatCurrency(item.price)}
                            </span>
                            {savingsPercent != null && (
                                <span style={savingsPillStyle}>{translations.savingsBadge} {savingsPercent}%</span>
                            )}
                        </>
                    )}
                     {item.unit_price != null && item.unit_price !== item.price && (
                        <span style={{
                            fontSize: '1em',
                            fontWeight: '700',
                            color: '#ff5722', // Color verde para precio alternativo
                            marginBottom: '4px'
                        }}>
                            {formatCurrency(item.unit_price)} / {translations.unit}
                    </span>
                    )}
                </div>

            </div>

        );
    }

    // --- RENDERIZADO EXTENDIDO PARA ARTÍCULO PRINCIPAL (RANK 1) ---

    // SHOP-179: same missing-enddate guard as the compact layout above -
    // formattedDate stays null (nothing rendered) instead of falling back to
    // the Unix epoch. Formatear la fecha según el idioma actual, solo si hay
    // una fecha real.
    const formattedDate = item.enddate
        ? parseDateOnlyAsLocal(item.enddate).toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        })
        : null;

    // Estilos para el principal
    const priceStyle = { fontSize: '1.4em', fontWeight: 'bold', color: isSelected ? '#00c853' : '#007bff' };

    return (
        <div >
            {/* Cabecera del Artículo */}
            <div className="card-header" style={{ marginBottom: '0px', marginRight:"5px", padding: '0px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h4 className="product-name" style={{
                       fontSize: '1.25em', fontWeight: 'bold'
                    }}>{item.brand?.trim() ? `${item.name} (${item.brand})` : item.name}</h4>

                </div>
                {/* SHOP-128/SHOP-172: price block, right-aligned with the name.
                    Active item: current price on top, regular_price
                    strikethrough + savings badge below (unchanged from
                    SHOP-128). Expired-with-regular_price item: the original
                    (stale) price struck through above the clean, current
                    regular_price - the reverse pairing, no savings badge. */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0 }}>
                    {isExpiredRegularPrice ? (
                        <>
                            {showOriginalPriceStrike && (
                                <span style={regularPriceStrikeStyle}>{formatCurrency(item.price)}</span>
                            )}
                            <span className="product-price" style={priceStyle}>
                                {formatCurrency(item.regular_price)}
                            </span>
                        </>
                    ) : (
                        <>
                            <span className="product-price" style={priceStyle}>
                                {formatCurrency(item.price)}
                            </span>
                            {savingsPercent != null && (
                                <span style={regularPriceStrikeStyle}>{formatCurrency(item.regular_price)}</span>
                            )}
                            {savingsPercent != null && (
                                <span style={savingsPillStyle}>{translations.savingsBadge} {savingsPercent}%</span>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Detalles */}
            <p className="product-detail"
                style={{
                       fontSize: '1em',
                       marginTop:"0px"
                }}
            >
                {item.detail}
            </p>

            {/* Metadatos */}
            <div className="product-meta" style={{fontSize: '1em', padding: '0' }}>
                <span><span className="icon">🛍️</span>{item.store}</span>
                <span><span className="icon">📦</span>{item.unit}</span>
                {hasSpecialOffer && <span><span className="icon">✨</span>{item.special}</span>}
                 {item.unit_price != null && item.unit_price !== item.price && (
                  <span
                    className="product-price"
                    style={{
                      ...priceStyle,
                      marginLeft: 'auto',
                      whiteSpace: 'nowrap',
                      fontSize: '1em',
                      fontWeight: '700',
                      color: '#ff5722',
                    }}
                  >
                    {formatCurrency(item.unit_price)} / {translations.unit}
                  </span>
                )}

            </div>


            {/* Fecha */}
            {formattedDate && (
                <p className="product-date" style={{fontSize: '1em', padding: '0',  textAlign:'left' }}>
                    {/* Uso de la traducción */}
                    <span className="icon" >📅</span>{formattedDate}
                </p>
            )}
        </div>
    );
};

export default ProductDetails;
